import { EventEmitter } from 'events';
import { toast } from 'react-toastify';
import React from 'react';

const API_BASE = process.env.REACT_APP_NOTIFICATION_API_BASE || 'https://notification.suite-one.fr';
const SECRET   = process.env.REACT_APP_NOTIFICATION_SECRET   || '';
const SYMFONY_API_URL = (process.env.REACT_APP_URL_BEONE_API || '');

const log = (...args: unknown[]) => console.warn('[NotifSSE]', ...args);

if (!SECRET) console.warn('[NotifSSE] REACT_APP_NOTIFICATION_SECRET manquant');

async function generateUserToken(userId: string, secret: string): Promise<string> {
  if (!window.crypto?.subtle) {
    const hash = btoa(userId + secret).replace(/[^a-zA-Z0-9]/g, '');
    return hash.substring(0, 32);
  }
  const encoder = new TextEncoder();
  const data = encoder.encode(userId + secret);
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

export interface Notification {
  id: string;
  message: string;
  type: string;
  status: 'ACTIVE' | 'ARCHIVED';
  severity: 'success' | 'warning' | 'error' | 'info';
  timestamp: string;
  read: boolean;
  queryId?: string;
}

interface NotificationAPIResponse {
  id: string | number;
  message: string;
  type: string;
  status: string;
  date?: string;
  read: boolean;
  queryId?: string | number;
}

interface ParsedCountMessage {
  action?: string;
  listCount?: Array<{ channel: string; count: number }> | null;
  message?: string;
}

function parseCountMessage(raw: string): ParsedCountMessage | null {
  if (!raw || !raw.trim().startsWith('{')) return null;
  try { return JSON.parse(raw) as ParsedCountMessage; } catch { return null; }
}

function shouldSkip(payload: NotificationAPIResponse): boolean {
  const t = (payload.type || '').trim();
  if (t.toUpperCase() === 'COUNT') {
    const parsed = parseCountMessage(payload.message);
    if (parsed && parsed.listCount === null) return true;
  }
  return false;
}

function normalizeMessageForKey(raw: string): string {
  const parsed = parseCountMessage(raw);
  if (parsed) {
    return JSON.stringify({
      action: parsed.action || '',
      message: parsed.message || '',
      listCount: parsed.listCount || null,
    });
  }

  return (raw || '').replace(/\s+/g, ' ').trim().toLowerCase();
}

function notificationBusinessKey(payload: Pick<NotificationAPIResponse, 'message' | 'type' | 'queryId'>): string {
  return [
    String(payload.queryId ?? ''),
    (payload.type || 'Info').trim().toUpperCase(),
    normalizeMessageForKey(payload.message || ''),
  ].join('|');
}

function normalizeNotificationTimestamp(raw?: string): string {
  if (!raw) return new Date().toISOString();

  const value = raw.trim();
  if (!value) return new Date().toISOString();

  const hasExplicitTimezone = /(?:z|[+-]\d{2}:?\d{2})$/i.test(value);
  const normalizedValue = value.includes('T') ? value : value.replace(' ', 'T');
  const date = new Date(hasExplicitTimezone ? normalizedValue : `${normalizedValue}Z`);

  return Number.isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
}

class NotificationServiceClass extends EventEmitter {
  private notifications: Notification[] = [];
  private readIds       = new Set<string>();
  private toastedIds    = new Set<string>();
  private seenBusinessKeys = new Map<string, string>();
  private toastedBusinessKeys = new Set<string>();
  private userId: string | null = null;
  private userToken: string | null = null;
  private notificationCenterOpen = false;

  private eventSource: EventSource | null = null;
  private connected    = false;
  private initialized  = false;
  private bootingPromise: Promise<boolean> | null = null;
  private reconnectAttempts = 0;

  constructor() {
    super();
    this.setMaxListeners(20);
  }


  async initialize(clientId: string): Promise<boolean> {
    if (!clientId) return false;

    if (this.initialized && this.userId === clientId) {
      if (!this.connected) {
        log('Stream fermé, réouverture...');
        this.openStream();
      }
      return true;
    }

    if (this.bootingPromise) {
      log('Boot déjà en cours, attente...');
      return this.bootingPromise;
    }

    this.userId = clientId;
    this.bootingPromise = this.boot().finally(() => { this.bootingPromise = null; });
    return this.bootingPromise;
  }

  /** Rafraîchit les notifs depuis REST uniquement — ne touche pas le stream SSE */
  async refreshFromRest(): Promise<void> {
    if (!this.userId) return;
    if (!this.userToken) {
      this.userToken = await generateUserToken(this.userId, SECRET);
    }
    await this.fetchFromRest();
  }

  markAsRead(notificationId: string): boolean {
    if (!notificationId) return false;
    this.readIds.add(notificationId);
    fetch(`${API_BASE}/api/webhook/${notificationId}/read`, { method: 'PATCH' }).catch(() => {});
    this.emit('notification-read', notificationId);
    this.emit('notifications-updated', this.notifications);
    return true;
  }

  markAllAsRead(): boolean {
    this.notifications
      .filter(n => !this.isRead(n.id))
      .forEach(n => {
        this.readIds.add(n.id);
        fetch(`${API_BASE}/api/webhook/${n.id}/read`, { method: 'PATCH' }).catch(() => {});
      });
    this.emit('all-notifications-read');
    this.emit('notifications-updated', this.notifications);
    return true;
  }

  archiveNotification(notificationId: string): boolean {
    if (!notificationId) return false;
    fetch(`${API_BASE}/api/webhook/${notificationId}/archive`, { method: 'PATCH' })
      .then(() => {
        const archived = this.notifications.find(n => n.id === notificationId);
        if (archived) {
          const key = notificationBusinessKey(archived);
          this.seenBusinessKeys.delete(key);
          this.toastedBusinessKeys.delete(key);
        }
        this.toastedIds.delete(notificationId);
        this.notifications = this.notifications.filter(n => n.id !== notificationId);
        this.emit('notifications-updated', this.notifications);
      })
      .catch(() => {});
    return true;
  }

  getNotifications()        { return this.notifications; }
  getUnreadCount()          { return this.notifications.filter(n => !this.isRead(n.id)).length; }
  isRead(id: string)        { return this.readIds.has(id); }
  isConnected()             { return this.connected; }

  setNotificationCenterState(isOpen: boolean) {
    this.notificationCenterOpen = isOpen;
    this.emit('notificationCenterStateChanged', isOpen);
  }

  openNotificationCenter(notificationId?: string) {
    toast.dismiss();
    this.emit('open-notification-center', notificationId);
    if (notificationId) {
      const notif = this.notifications.find(n => n.id === notificationId);
      this.emit('open-notification-center-tab', notif && !this.isRead(notificationId) ? 0 : 1);
      setTimeout(() => this.emit('scroll-to-notification', notificationId), 100);
    }
  }

  getConnectionStatus() {
    return {
      connected: this.connected,
      userId:    this.userId,
      notificationsCount: this.notifications.length,
      unreadCount: this.getUnreadCount(),
    };
  }

  disconnect() {
    this.closeStream();
    this.connected = false;
    this.emit('connection-status', false);
  }


  private async boot(): Promise<boolean> {
    try {
      this.userToken = await generateUserToken(this.userId!, SECRET);
      log(`Token : ${this.userToken.substring(0, 8)}...`);
      await this.fetchFromRest();
      this.openStream();
      this.initialized = true;
      return true;
    } catch (err) {
      log('Erreur boot:', err);
      return false;
    }
  }

  private openStream() {
    this.closeStream();

    const streamUrl = `${SYMFONY_API_URL}/notifications/stream?token=${encodeURIComponent(this.userToken!)}`;
    log(`Ouverture SSE → ${streamUrl}`);

    const es = new EventSource(streamUrl, { withCredentials: true });
    this.eventSource = es;

    es.addEventListener('connected', () => {
      this.connected = true;
      this.reconnectAttempts = 0;
      log('SSE connecté ✅');
      this.emit('connection-status', true);
      this.fetchFromRest().catch(() => {});
    });

    es.addEventListener('notification', (e: MessageEvent) => {
      log('SSE notif reçue:', e.data.substring(0, 120));
      this.handleIncoming(e.data);
    });

    es.addEventListener('reconnect', () => {
      log('SSE: reconnexion demandée par le serveur');
    });

    es.onerror = () => {
      this.connected = false;
      this.emit('connection-status', false);
      this.reconnectAttempts++;
      log(`SSE erreur — reconnexion automatique (tentative #${this.reconnectAttempts})`);
    };
  }

  private closeStream() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  private async fetchFromRest(): Promise<void> {
    if (!this.userToken) return;
    try {
      const res = await fetch(`${API_BASE}/api/webhook/user/${this.userToken}`);
      if (!res.ok) { log(`REST ${res.status}`); return; }
      const data: NotificationAPIResponse[] = await res.json();
      const active = data
        .filter(n => n.status !== 'ARCHIVED' && !shouldSkip(n))
        .map((n): Notification => ({
          id:        String(n.id),
          message:   n.message,
          type:      n.type,
          status:    (n.status === 'ACTIVE' || n.status === 'ARCHIVED' ? n.status : 'ACTIVE') as 'ACTIVE' | 'ARCHIVED',
          severity:  this.severityFromType(n.type),
          timestamp: normalizeNotificationTimestamp(n.date),
          read:      n.read,
          queryId:   n.queryId != null ? String(n.queryId) : undefined,
        }));

      const dedupedActive = active.reduce<Notification[]>((acc, notif) => {
        const key = notificationBusinessKey(notif);
        const existingIndex = acc.findIndex((n) => notificationBusinessKey(n) === key);
        if (existingIndex === -1) {
          acc.push(notif);
        } else if (new Date(notif.timestamp).getTime() >= new Date(acc[existingIndex].timestamp).getTime()) {
          acc[existingIndex] = notif;
        }
        return acc;
      }, []);

      log(`REST: ${dedupedActive.length} notifications`);
      this.notifications = dedupedActive;
      dedupedActive.forEach(n => {
        if (n.read) this.readIds.add(n.id);
        this.seenBusinessKeys.set(notificationBusinessKey(n), n.id);
      });
      this.emit('notifications-updated', this.notifications);
    } catch (err) {
      log('REST erreur:', err);
    }
  }

  private handleIncoming(raw: string) {
    try {
      const payload: NotificationAPIResponse = JSON.parse(raw);
      if ((payload.status ?? '') === 'ARCHIVED') return;

      if (payload.id == null || payload.id === '') return;

      if (shouldSkip(payload)) return;
      const id = String(payload.id);
      const businessKey = notificationBusinessKey(payload);

      if (this.notifications.some(n => n.id === id)) return;

      const previousIdForSameEvent = this.seenBusinessKeys.get(businessKey);
      if (previousIdForSameEvent) {
        this.toastedIds.add(id);
        this.toastedBusinessKeys.add(businessKey);
        this.notifications = this.notifications.map((n) => (
          n.id === previousIdForSameEvent
            ? {
                ...n,
                id,
                message: payload.message,
                type: payload.type || 'Info',
                severity: this.severityFromType(payload.type),
                timestamp: payload.date ? normalizeNotificationTimestamp(payload.date) : n.timestamp,
                queryId: payload.queryId != null ? String(payload.queryId) : n.queryId,
              }
            : n
        ));
        this.seenBusinessKeys.set(businessKey, id);
        this.emit('notifications-updated', this.notifications);
        return;
      }

      const notif: Notification = {
        id,
        message:   payload.message,
        type:      payload.type || 'Info',
        status:    'ACTIVE',
        severity:  this.severityFromType(payload.type),
        timestamp: normalizeNotificationTimestamp(payload.date),
        read:      false,
        queryId:   payload.queryId != null ? String(payload.queryId) : undefined,
      };

      this.seenBusinessKeys.set(businessKey, id);
      this.notifications.unshift(notif);
      if (this.notifications.length > 100) this.notifications = this.notifications.slice(0, 100);

      this.emit('notifications-updated', this.notifications);
      this.emit('new-notification', notif);

      if (!this.toastedIds.has(id) && !this.toastedBusinessKeys.has(businessKey)) {
        this.toastedIds.add(id);
        this.toastedBusinessKeys.add(businessKey);
        this.showToast(notif);
      }
    } catch (_) {}
  }

  private severityFromType(type: string): 'success' | 'warning' | 'error' | 'info' {
    const t = (type || '').toLowerCase();
    if (t.includes('error'))   return 'error';
    if (t.includes('warning')) return 'warning';
    if (t.includes('success')) return 'success';
    return 'info';
  }

  private showToast(notif: Notification) {
    if (this.notificationCenterOpen) return;

    const parsed = parseCountMessage(notif.message);
    const displayMsg = parsed?.message || notif.message;
    const isCount = (notif.type || '').toUpperCase() === 'COUNT';
    const hasQuery = !!notif.queryId;

    const handleViewResults = (e: React.MouseEvent) => {
      e.stopPropagation();
      toast.dismiss(notif.id);
      this.emit('open-query-result-dialog', notif.queryId);
    };

    const content = (
      <div style={{ lineHeight: 1.4 }}>
        <div style={{ fontWeight: 700, fontSize: '0.875rem', marginBottom: 3 }}>
          {notif.type}
          {hasQuery && (
            <span style={{ fontWeight: 400, fontSize: '0.72rem', opacity: 0.75, marginLeft: 6 }}>
              · Requête #{notif.queryId}
            </span>
          )}
        </div>
        <div style={{ fontSize: '0.82rem' }}>{displayMsg}</div>
        {parsed?.listCount && parsed.listCount.length > 0 && (
          <div style={{ fontSize: '0.72rem', marginTop: 4, opacity: 0.85 }}>
            {parsed.listCount
              .map((c) => `${c.channel} : ${c.count.toLocaleString('fr-FR')}`)
              .join('  ·  ')}
          </div>
        )}
        {isCount && hasQuery && (
          <button
            onClick={handleViewResults}
            style={{
              marginTop: 8,
              padding: '3px 12px',
              background: 'rgba(255,255,255,0.25)',
              border: '1px solid rgba(255,255,255,0.55)',
              borderRadius: 4,
              color: 'inherit',
              cursor: 'pointer',
              fontSize: '0.75rem',
              fontWeight: 600,
            }}
          >
            Voir les résultats →
          </button>
        )}
      </div>
    );

    const opts = {
      autoClose: isCount ? 8000 : 5000,
      toastId: notif.id,
      onClick: () => this.openNotificationCenter(notif.id),
    };

    switch (notif.severity) {
      case 'error':   toast.error(content, opts);   break;
      case 'warning': toast.warning(content, opts); break;
      case 'success': toast.success(content, opts); break;
      default:        toast.info(content, opts);
    }
  }
}

const NotificationService = new NotificationServiceClass();

window.addEventListener('beforeunload', () => {
  NotificationService.disconnect();
  NotificationService.removeAllListeners();
});

window.addEventListener('online', () => {
  if (!NotificationService.isConnected()) {
    setTimeout(() => {
      NotificationService.initialize(NotificationService.getConnectionStatus().userId || '');
    }, 1000);
  }
});

export default NotificationService;
